import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { FeaturedSeasonalGiftsIcon, PackageIcon } from "../icons";

export interface SendGiftModalProps {
  open: boolean;
  onClose: () => void;
  onSendGiftOnly: () => void;
  onSendCollection: () => void;
}

interface OptionCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  testId: string;
  onClick: () => void;
}

function OptionCard({ icon, title, subtitle, testId, onClick }: OptionCardProps) {
  return (
    <Box
      component="button"
      data-testid={testId}
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "30px",
        width: "100%",
        p: 2,
        border: "1px solid var(--color-smoke)",
        borderRadius: "16px",
        bgcolor: "white",
        cursor: "pointer",
        textAlign: "left",
        transition: "border-color 0.15s, background-color 0.15s",
        "&:hover": {
          borderColor: "var(--color-dark-dust)",
          bgcolor: "var(--color-good-ochre-bg)",
        },
        "&:focus-visible": {
          outline: "2px solid var(--color-dark-dust)",
          outlineOffset: "2px",
        },
      }}
    >
      {/* Icon — gray rounded-square background, no border */}
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "10px",
          bgcolor: "var(--color-wax)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: "#555",
        }}
        aria-hidden="true"
      >
        {icon}
      </Box>

      {/* Text */}
      <Box>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 700,
            lineHeight: 1.3,
            color: "var(--color-rodeo-black)",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: 1.4,
            color: "var(--color-graphite)",
            mt: "4px",
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
}

export default function SendGiftModal({
  open,
  onClose,
  onSendGiftOnly,
  onSendCollection,
}: SendGiftModalProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableScrollLock
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            border: "1px solid var(--color-smoke)",
            p: 2,
            m: 2,
          },
        },
      }}
    >
      {/* Close button — 24px from top and right */}
      <IconButton
        aria-label="Close modal"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: "24px",
          top: "24px",
          color: "var(--color-rodeo-black)",
          p: 0,
          "&:focus-visible": { outline: "2px solid var(--color-dark-dust)", outlineOffset: "2px" },
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Header: circular icon bg + title */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "10px",
          mb: 3,
          pt: 1,
        }}
      >
        {/* Gift icon on a gray circle */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            bgcolor: "var(--color-wax)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          aria-hidden="true"
        >
          <FeaturedSeasonalGiftsIcon sx={{ fontSize: 32, color: "#555" }} />
        </Box>

        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            lineHeight: 1.3,
            color: "var(--color-rodeo-black)",
            textAlign: "center",
          }}
        >
          Gift Sending Options
        </Typography>
      </Box>

      {/* Option cards — 24px gap */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <OptionCard
          testId="send-gift-only-btn"
          icon={<FeaturedSeasonalGiftsIcon sx={{ fontSize: 22 }} />}
          title="Send This Gift Only"
          subtitle="Can be swapped for another gift by recipients"
          onClick={onSendGiftOnly}
        />
        <OptionCard
          testId="send-collection-btn"
          icon={<PackageIcon sx={{ fontSize: 28 }} />}
          title="Send The Collection"
          subtitle="Let your recipient choose from the full collection"
          onClick={onSendCollection}
        />
      </Box>
    </Dialog>
  );
}
